import type { PlanId } from './plans';

/**
 * Plan prices in several currencies. USD is the base currency and always present; the others
 * are shown to visitors from the matching countries. Amounts are in minor units (cents, kopiyky…).
 */
export const CURRENCIES = ['USD', 'EUR', 'UAH', 'PLN', 'RON', 'MDL'] as const;
export type Currency = (typeof CURRENCIES)[number];
export const BASE_CURRENCY: Currency = 'USD';

export type PaidPlanId = Exclude<PlanId, 'free'>;
export const PAID_PLANS: PaidPlanId[] = ['starter', 'pro', 'business'];

export interface PlanPrice {
  month: number;
  year: number;
}
export type CurrencyPrices = Record<PaidPlanId, PlanPrice>;
/** USD is required; a currency without prices is not offered. */
export type PriceTable = { USD: CurrencyPrices } & Partial<Record<Currency, CurrencyPrices>>;

const p = (month: number, year: number): PlanPrice => ({ month: month * 100, year: year * 100 });

/** Starting prices; superadmins change them in the admin panel. */
export const DEFAULT_PRICES: PriceTable = {
  USD: { starter: p(19, 190), pro: p(49, 490), business: p(149, 1490) },
  EUR: { starter: p(18, 180), pro: p(45, 450), business: p(139, 1390) },
  UAH: { starter: p(799, 7990), pro: p(1990, 19900), business: p(5990, 59900) },
  PLN: { starter: p(79, 790), pro: p(199, 1990), business: p(599, 5990) },
  RON: { starter: p(89, 890), pro: p(229, 2290), business: p(699, 6990) },
  MDL: { starter: p(349, 3490), pro: p(899, 8990), business: p(2690, 26900) },
};

export function isCurrency(value: unknown): value is Currency {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value);
}

/** Price of a plan in a currency (0 for Free, base currency when the currency isn't offered). */
export function planPrice(
  table: PriceTable,
  plan: PlanId,
  interval: 'month' | 'year',
  currency: Currency = BASE_CURRENCY,
): { amount: number; currency: Currency } {
  if (plan === 'free') return { amount: 0, currency };
  const row = table[currency] ?? table.USD;
  return { amount: row[plan][interval], currency: table[currency] ? currency : 'USD' };
}

const EURO_COUNTRIES = new Set(
  'AT BE HR CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES MC SM VA AD ME XK'.split(' '),
);

/** Currency for an ISO country code (from a CDN geo header). */
export function currencyForCountry(country: string | null | undefined): Currency | null {
  const c = (country ?? '').toUpperCase();
  if (!c) return null;
  if (c === 'UA') return 'UAH';
  if (c === 'PL') return 'PLN';
  if (c === 'RO') return 'RON';
  if (c === 'MD') return 'MDL';
  if (EURO_COUNTRIES.has(c)) return 'EUR';
  return 'USD';
}

const TZ_CURRENCY: Record<string, Currency> = {
  'Europe/Kyiv': 'UAH',
  'Europe/Kiev': 'UAH',
  'Europe/Uzhgorod': 'UAH',
  'Europe/Zaporozhye': 'UAH',
  'Europe/Warsaw': 'PLN',
  'Europe/Bucharest': 'RON',
  'Europe/Chisinau': 'MDL',
  'Europe/Tiraspol': 'MDL',
};
const EURO_ZONES = new Set(
  (
    'Vienna Brussels Zagreb Tallinn Helsinki Paris Berlin Busingen Athens Dublin Rome Riga ' +
    'Vilnius Luxembourg Malta Amsterdam Lisbon Bratislava Ljubljana Madrid Monaco Andorra ' +
    'San_Marino Vatican Podgorica Nicosia'
  )
    .split(' ')
    .map((city) => `Europe/${city}`)
    .concat([
      'Asia/Nicosia',
      'Asia/Famagusta',
      'Atlantic/Madeira',
      'Atlantic/Azores',
      'Atlantic/Canary',
      'Africa/Ceuta',
    ]),
);

/** Fallback guess from the browser time zone when there is no geo header. */
export function currencyForTimeZone(tz: string | null | undefined): Currency | null {
  if (!tz) return null;
  if (TZ_CURRENCY[tz]) return TZ_CURRENCY[tz];
  if (EURO_ZONES.has(tz)) return 'EUR';
  return null;
}

/** "$19", "799 ₴", "79 zł" — symbols, and no decimals for whole amounts. */
export function formatPrice(amount: number, currency: string, localeTag = 'en-US'): string {
  const major = amount / 100;
  return new Intl.NumberFormat(localeTag, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(major);
}
